import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Chip,
  IconButton,
  Pagination,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  Card,
  CardContent,
  useTheme,
  Drawer,
  Divider,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import httpRequest from "../utils/httpRequest";

const TransactionModal = ({
  open,
  onClose,
  transactions,
  customerName,
  page,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEditTransaction,
  onTransactionDeleted,
  onReopen,
  onDateFilterChange,
  customerId,
}) => {
  const theme = useTheme();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editForm, setEditForm] = useState({
    amount: "",
    description: "",
    type: "",
    transaction_date: "",
  });
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const endDateRef = React.useRef(null);

  // Use transactions directly since filtering is now done on backend
  const filteredTransactions = transactions || [];

  const handleDateFilterChange = (field, value) => {
    const newDateFilter = {
      ...dateFilter,
      [field]: value
    };
    
    setDateFilter(newDateFilter);
    
    // Auto-focus on end date field when start date is selected
    if (field === 'startDate' && value && endDateRef.current) {
      setTimeout(() => {
        endDateRef.current.focus();
      }, 100);
    }
    
    // Trigger backend filter if callback is provided
    if (onDateFilterChange && customerId) {
      onDateFilterChange(newDateFilter);
    }
  };

  const clearDateFilter = () => {
    const clearedFilter = {
      startDate: "",
      endDate: "",
    };
    setDateFilter(clearedFilter);
    
    // Trigger backend filter with cleared dates
    if (onDateFilterChange && customerId) {
      onDateFilterChange(clearedFilter);
    }
  };

  // Reset date filter when modal closes
  React.useEffect(() => {
    if (!open) {
      setDateFilter({
        startDate: "",
        endDate: "",
      });
    }
  }, [open]);

  const handleEditClick = (transaction) => {
    setSelectedTransaction(transaction);
    setEditForm({
      amount: transaction.amount,
      description: transaction.description || "",
      type: transaction.type,
      transaction_date: new Date(transaction.transaction_date).toISOString().split('T')[0],
    });
    onClose();
    setEditDrawerOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      await onEditTransaction(selectedTransaction.id, editForm);
      setEditDrawerOpen(false);
      setSelectedTransaction(null);
      if (typeof onReopen === 'function') onReopen();
    } catch (error) {
      console.error("Error editing transaction:", error);
    }
  };

  const handleEditClose = () => {
    setEditDrawerOpen(false);
    setSelectedTransaction(null);
    if (typeof onReopen === 'function') onReopen();
  };

  const handleDeleteClick = (transaction) => {
    setSelectedTransaction(transaction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await httpRequest(`customers/transactions/${selectedTransaction.id}`, "DELETE");
      setSnackbar({
        open: true,
        message: "Transaction deleted successfully",
        severity: "success",
      });
      onTransactionDeleted();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete transaction",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle 
          sx={{ 
            pb: 0.5, 
            px: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" sx={{ mb: 0.25, color: 'inherit' }}>
                Transactions for {customerName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.8 }}>
                {transactions?.length || 0} transaction{(transactions?.length || 0) !== 1 ? 's' : ''} shown
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: 'inherit' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, maxHeight: "75vh", overflowY: "auto", mt: 2 }}>
                    {/* Compact Date Filter */}
          <Box sx={{ 
            mb: 1, 
            p: 1, 
            bgcolor: 'grey.50', 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              <FilterListIcon fontSize="small" color="primary" />
              <Typography variant="caption" fontWeight={600} sx={{ color: 'text.secondary' }}>
                Date Filter
              </Typography>
              {(dateFilter.startDate || dateFilter.endDate) && (
                <Chip 
                  size="small" 
                  label={`${filteredTransactions.length}`}
                  color="primary"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                size="small"
                placeholder="From"
                type="date"
                value={dateFilter.startDate}
                onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  minWidth: 120,
                  '& .MuiInputBase-root': { height: 32 },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                }}
                inputProps={{
                  max: dateFilter.endDate || undefined
                }}
                onClick={(e) => e.target.showPicker?.()}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                to
              </Typography>
              <TextField
                size="small"
                placeholder="To"
                type="date"
                value={dateFilter.endDate}
                onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  minWidth: 120,
                  '& .MuiInputBase-root': { height: 32 },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                }}
                inputRef={endDateRef}
                inputProps={{
                  min: dateFilter.startDate || undefined
                }}
                onClick={(e) => e.target.showPicker?.()}
              />
              {(dateFilter.startDate || dateFilter.endDate) && (
                <Button
                  size="small"
                  variant="text"
                  onClick={clearDateFilter}
                  sx={{ 
                    minWidth: 'auto', 
                    px: 0.5, 
                    py: 0.25,
                    fontSize: '0.7rem',
                    height: 24
                  }}
                >
                  ×
                </Button>
              )}
            </Box>
          </Box>

          {filteredTransactions && filteredTransactions.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {filteredTransactions.map((txn) => (
                <Card key={txn.id} variant="outlined" sx={{ borderLeft: `4px solid ${txn.type === "credit" ? theme.palette.success.main : theme.palette.error.main}` }}>
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Chip
                          size="small"
                          label={txn.type.toUpperCase()}
                          color={txn.type === "credit" ? "success" : "error"}
                          sx={{ fontWeight: 600 }}
                        />
                        <Typography variant="h6" fontWeight={600} color={txn.type === "credit" ? "success.main" : "error.main"}>
                          ₹{txn.amount.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit transaction">
                          <IconButton size="small" onClick={() => handleEditClick(txn)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete transaction">
                          <IconButton size="small" onClick={() => handleDeleteClick(txn)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {txn.description || "No description"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(txn.transaction_date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <Typography variant="body2" color="text.secondary">
                {dateFilter.startDate || dateFilter.endDate 
                  ? "No transactions found for the selected date range." 
                  : "No transactions found."}
              </Typography>
            </Box>
          )}
        </DialogContent>

        {filteredTransactions && filteredTransactions.length > 0 && (
          <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, val) => onPageChange(val)}
              size="small"
              color="primary"
            />
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel id="page-size-label">Rows</InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                label="Rows"
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
              >
                {[5, 10, 20, 50].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Alert severity="warning">
              Are you sure you want to delete this transaction?
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Dialog>

      <Drawer
        anchor="right"
        open={editDrawerOpen}
        onClose={handleEditClose}
        hideBackdrop
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 400 },
            zIndex: (theme) => theme.zIndex.modal + 2,
          },
        }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2, bgcolor: 'grey.50' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                Edit Transaction
              </Typography>
              {customerName && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {customerName}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleEditClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Transaction Type</InputLabel>
              <Select
                value={editForm.type}
                label="Transaction Type"
                onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
              >
                <MenuItem value="credit">Credit</MenuItem>
                <MenuItem value="debit">Debit</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={editForm.amount}
              onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
              inputProps={{ min: 0 }}
            />
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={editForm.transaction_date}
              onChange={(e) => setEditForm({ ...editForm, transaction_date: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              label="Description"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              multiline
              rows={2}
            />
          </Box>
          <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={handleEditClose}>Cancel</Button>
            <Button onClick={handleEditSubmit} variant="contained" color="primary">
              Save Changes
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default TransactionModal;
